import { useEffect, useRef } from 'react';
import type { OnEmulatorAction } from '@dorian/creation-tools-ui';
import { FEATURE_NODE_EDIT_SIDE_PANEL } from '../../../helpers/featureFlags';
import { CursorContextValue } from '../../../contexts/Cursor/CursorContext';
import { CursorPosition } from '../../../contexts/Cursor/cursorReducer';
import { NodeGetter } from './getNode';
import { ResolveForwardStepDeps, resolveBackStep, resolveForwardStep } from './resolveForwardStep';

type ActionPort = Pick<CursorContextValue, 'goForward' | 'goBack' | 'navigate'>;

interface ActionStore {
  setOnAction(handler: OnEmulatorAction | null): void;
}

export function createEmulatorActionHandler(
  getCursor: () => CursorPosition | null,
  nodeGetter: NodeGetter,
  port: ActionPort,
  deps: ResolveForwardStepDeps,
  getBackTarget: () => CursorPosition | null = () => null,
): OnEmulatorAction {
  let inFlight = false;

  return function handleEmulatorAction(action) {
    if (action.type === 'back') {
      const backTarget = getBackTarget();
      // Roll back variable state before navigating so back+forward through
      // Remember/Random/Check does not apply the effect twice.
      if (backTarget) {
        const targetNode = nodeGetter(backTarget.branchId);
        if (targetNode) resolveBackStep(targetNode, backTarget, deps);
      }
      port.goBack();
      return;
    }
    if (action.type === 'goto') {
      port.navigate(action.branchId);
      return;
    }
    if (inFlight) return;
    const cursor = getCursor();
    if (!cursor) return;
    const node = nodeGetter(cursor.branchId);
    if (!node) {
      port.goForward(null);
      return;
    }

    inFlight = true;
    resolveForwardStep(node, cursor, deps)
      .then((resolution) => {
        // Superseded guard: a technical step (Check/Remember/Random) can await a popup
        // before resolving. If the author navigates away while it is open (clicks
        // another node, which dismisses the popup), the cursor has already moved.
        // Applying this now-stale resolution would fight that navigation, so drop it.
        const latest = getCursor();
        if (!latest || latest.branchId !== cursor.branchId || latest.stepIndex !== cursor.stepIndex) {
          return;
        }
        if (resolution.type === 'navigate') {
          port.navigate(resolution.branchId);
        } else if (resolution.type === 'forward') {
          port.goForward(resolution.fallback);
        }
        // 'stop': intentional no-op — navigator leaves the user at the current step
      })
      .catch(() => {
        // resolution error: stop rather than navigate to an unknown position
        port.goForward(null);
      })
      .finally(() => {
        inFlight = false;
      });
  };
}

export function useEmulatorActionHandler(
  store: ActionStore | null,
  cursorCtx: Pick<CursorContextValue, 'cursor' | 'historyBack' | 'goForward' | 'goBack' | 'navigate'> | null,
  nodeGetter: NodeGetter,
  deps: ResolveForwardStepDeps | null,
): void {
  // refs keep the registered handler reading fresh values without re-registering
  const cursorRef = useRef<CursorPosition | null>(cursorCtx?.cursor ?? null);
  cursorRef.current = cursorCtx?.cursor ?? null;
  const historyBackRef = useRef(cursorCtx?.historyBack ?? []);
  historyBackRef.current = cursorCtx?.historyBack ?? [];

  const nodeGetterRef = useRef(nodeGetter);
  nodeGetterRef.current = nodeGetter;

  const depsRef = useRef(deps);
  depsRef.current = deps;

  useEffect(() => {
    if (!FEATURE_NODE_EDIT_SIDE_PANEL) return undefined;
    if (!store || !cursorCtx || !deps) return undefined;

    const { goForward, goBack, navigate } = cursorCtx;
    const port: ActionPort = { goForward, goBack, navigate };

    const handler = createEmulatorActionHandler(
      () => cursorRef.current,
      (branchId) => nodeGetterRef.current(branchId),
      port,
      // snapshot deps at registration; choiceMemory/popupComponent are stable refs
      deps,
      () => historyBackRef.current[historyBackRef.current.length - 1] ?? null,
    );

    store.setOnAction(handler);
    return () => {
      store.setOnAction(null);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- depend on the stable port functions, not the context object identity
  }, [store, cursorCtx?.goForward, cursorCtx?.goBack, cursorCtx?.navigate, deps]);
}
